// Parse a Flow YAML string into the node/edge shape consumed by @xyflow/react.
// Schema is documented in @cairntools/schema (V1: cairntools.com/v1alpha1).
//
// We don't enforce the full schema here — the YAML written to Firestore is
// already validated by the worker / CLI before storage. We only need enough
// structure to build the visual graph.

import { parse as parseYaml } from 'yaml'

const NODE_WIDTH = 220
const NODE_HEIGHT = 88

/**
 * @param {string} yamlText
 * @returns {{
 *   nodes: Array<{ id: string, data: object, position: {x:number,y:number}, width:number, height:number }>,
 *   edges: Array<{ id: string, source: string, target: string, label?: string, data?: object }>,
 *   entry: string | null,
 *   meta: object,
 * }}
 */
export function parseFlowYaml(yamlText) {
  if (!yamlText || typeof yamlText !== 'string') {
    return { nodes: [], edges: [], entry: null, meta: {} }
  }

  let parsed
  try {
    parsed = parseYaml(yamlText)
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    const e = new Error(`Flow YAML failed to parse: ${message}`)
    e.cause = err
    throw e
  }

  const spec = parsed?.spec
  if (!spec || typeof spec !== 'object') {
    return { nodes: [], edges: [], entry: null, meta: parsed?.metadata ?? {} }
  }

  const rawNodes = spec.nodes ?? {}
  const entry = typeof spec.entry === 'string' ? spec.entry : null

  const nodes = Object.entries(rawNodes).map(([id, body]) => ({
    id,
    data: {
      label: body?.title ?? id,
      route: body?.route,
      asserts: body?.asserts ?? [],
      inputs: body?.inputs ?? [],
      transitions: body?.transitions ?? [],
      fingerprint: body?.fingerprint ?? null,
      isEntry: id === entry,
      raw: body,
    },
    // Real positions are filled in by dagre. Initial placeholder keeps React
    // Flow happy before layout runs.
    position: { x: 0, y: 0 },
    width: NODE_WIDTH,
    height: NODE_HEIGHT,
  }))

  const edges = []
  for (const [id, body] of Object.entries(rawNodes)) {
    const transitions = body?.transitions ?? []
    for (const t of transitions) {
      const target = t?.to ?? t?.target
      if (!target) continue
      edges.push({
        id: `${id}->${target}-${t?.on ?? 'default'}`,
        source: id,
        target,
        label: t?.on,
        data: { raw: t },
      })
    }
  }

  return {
    nodes,
    edges,
    entry,
    meta: parsed?.metadata ?? {},
  }
}

export { NODE_WIDTH, NODE_HEIGHT }
